import { useEffect, useRef, useState } from "react"
import ScreenWrapper from "@/components/ScreenWrapper"
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router"
import {
  Button,
  DetailSidebar,
  downloadFromUrl,
  LayoutWithSidebar,
  Separator,
  useToast,
} from "toptaal-shared"
import SignatureCanvas from "react-signature-canvas"
import { TrashIcon } from "@heroicons/react/24/outline"
import {
  useSignStudentInvoiceMutation,
  useStudentInvoiceDocumentQuery,
  useStudentInvoiceOpenedAtTimesMutation,
} from "students-api"
import { PDFViewer } from "@/components/PDFViewer"
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid"

export const Route = createFileRoute(
  "/$invoiceId/$uploadId/$invoiceName/factuur-ondertekenen",
)({
  component: RouteComponent,
})

function RouteComponent() {
  const router = useRouter()
  const { success } = useToast()

  const { invoiceId, uploadId, invoiceName } = useParams({
    from: "/$invoiceId/$uploadId/$invoiceName/factuur-ondertekenen",
  })

  const sigRef = useRef<SignatureCanvas | null>(null)
  const [hasSigned, setHasSigned] = useState(false)

  const mutation = useSignStudentInvoiceMutation()
  const appendOpenedAtTimesMutation = useStudentInvoiceOpenedAtTimesMutation()

  useEffect(() => {
    appendOpenedAtTimesMutation.mutate(Number(invoiceId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId])

  const { data: invoice } = useStudentInvoiceDocumentQuery({
    invoiceId: Number(invoiceId),
    uploadId: Number(uploadId),
  })

  const clear = () => {
    sigRef.current?.clear()
    setHasSigned(false)
  }

  const handleSubmit = () => {
    if (!sigRef.current) return
    const signature = sigRef.current.toDataURL()

    mutation
      .mutateAsync({
        invoiceId: Number(invoiceId),
        date: new Date().toLocaleDateString(),
        signature,
      })
      .then(() => {
        router.navigate({ to: "/home/facturen", replace: true })
        success("Factuur succesvol ondertekend.")
      })
  }

  const onDownloadClick = () => {
    if (invoice?.data?.url) {
      downloadFromUrl(invoice.data.url, `${invoiceName}.pdf`)
    }
  }

  return (
    <ScreenWrapper className="!px-0">
      <LayoutWithSidebar
        rightPanelClassName="px-0 py-4"
        sidebar={
          <DetailSidebar goBackLabel="Factuur ondertekenen">
            <div className="space-y-3">
              <div>
                <div className="rounded-xl bg-white overflow-hidden w-full relative mb-8">
                  <SignatureCanvas
                    ref={(ref) => {
                      sigRef.current = ref
                    }}
                    penColor="#000000"
                    canvasProps={{
                      height: 250,
                      width: 450,
                    }}
                    onBegin={() => setHasSigned(true)}
                  />
                  <button
                    className="w-fit absolute right-4 top-4"
                    onClick={clear}
                  >
                    <TrashIcon className="size-5 text-gray-400 hover:text-primary-dark-blue transition-all duration-200 cursor-pointer" />
                  </button>
                </div>

                <div className="flex gap-2 mt-8 justify-end">
                  <Button
                    className="w-fit"
                    onClick={handleSubmit}
                    disabled={!hasSigned || mutation.isPending}
                  >
                    Ondertekenen
                  </Button>
                </div>
              </div>
            </div>
          </DetailSidebar>
        }
      >
        <div className="flex justify-between px-8">
          {invoiceName}

          <ArrowDownTrayIcon
            className="size-5 hover:text-primary-green transition-colors duration-150 cursor-pointer mb-4"
            onClick={onDownloadClick}
          />
        </div>
        <Separator className="mb-4" />
        {invoice && <PDFViewer file={invoice.data.url} />}
      </LayoutWithSidebar>
    </ScreenWrapper>
  )
}
